// Copyright 2025 Sorama B.V.

import { ReportableMeasurement } from "datatypes/ReportableMeasurement";
import { SorXLDData, SorXMeasurement } from "datatypes/SorXMeasurement";
import { CompositeUnit } from "units/CompositeUnit";
import { Currency } from "units/Currency";
import { Unit, UnitValue } from "units/Unit";
import { UnitParser } from "units/UnitParser";
import { UnitSystem } from "units/UnitSystem";
import { Volume } from "units/Volume";

/**
 * Static helper class for unit system and currency management in measurements
 */
export class MeasurementUnitHelpers {
    /**
     * Rehydrates plain objects that were UnitValue instances before cloning (e.g. via structuredClone).
     * After cloning, UnitValue instances lose their prototype and getters. This method walks the data
     * and restores them to proper UnitValue instances using UnitParser to look up the Unit singletons.
     */
    public static rehydrateUnitValues(data: Record<string, unknown>): void {
        if (!data) return;

        for (const key of Object.keys(data)) {
            const value = data[key] as Record<string, unknown>;
            if (
                value &&
                typeof value === "object" &&
                "value" in value &&
                "_unit" in value &&
                !(value instanceof UnitValue)
            ) {
                const plainUnit = value._unit as Record<string, unknown>;
                const unitString = typeof plainUnit === "string" ? plainUnit : (plainUnit?._unit as string);
                if (unitString) {
                    const unit = UnitParser.Parse(unitString);
                    if (unit) {
                        data[key] = new UnitValue(value.value as number, unit);
                    }
                }
            }
        }
    }

    /**
     * Determines the unit system from a measurement, defaulting to Metric if not specified
     */
    public static determineUnitSystem(measurement: ReportableMeasurement<SorXMeasurement> | null): UnitSystem {
        let unitSystem = UnitSystem.Metric; // Default to Metric

        if (measurement?.data && measurement.type === "SORXLD") {
            const data = measurement.data as SorXLDData;

            if (data.tagLeakUnitSystem) {
                unitSystem = data.tagLeakUnitSystem;
            } else if (data.tagLeakLinePressure?.unit && data.tagLeakGasCost?.unit) {
                // If unit is defined, we can determine the unit system based on the gas cost unit
                const gasVolumeUnit = (data.tagLeakGasCost.unit as CompositeUnit).denominator;
                if (Volume.imperialVolumeUnits.includes(gasVolumeUnit)) {
                    unitSystem = UnitSystem.Imperial;
                }
            }
        }

        return unitSystem;
    }

    /**
     * Determines the currency from a measurement, defaulting to EUR if not specified
     */
    public static determineCurrency(measurement: ReportableMeasurement<SorXMeasurement> | null): Unit {
        let currency = Currency.EUR; // Default to EUR

        if (measurement?.data && measurement.type === "SORXLD") {
            const data = measurement.data as SorXLDData;
            if (data.tagLeakCost?.unit) {
                currency = data.tagLeakCost.unit;
            }
        }

        return currency;
    }

    /**
     * Set all units in measurement data to the default units of the specified unit system.
     * Values are converted accordingly to the unit change
     */
    public static applyUnitSystemDefaults(measurementData: Record<string, unknown>, unitSystem: UnitSystem): void {
        if (!measurementData || !unitSystem) return;

        for (const key of Object.keys(measurementData)) {
            const value = measurementData[key];
            if (value instanceof UnitValue) {
                for (const unitKey of Object.keys(unitSystem)) {
                    const unit = unitSystem[unitKey];
                    if (unit instanceof Unit && unit.isOfSameUnitFamily(value.unit)) {
                        if (!value.unit.isLinearUnit) break;
                        measurementData[key] = new UnitValue(value.toUnit(unit), unit);
                    }
                }
            }
        }
    }

    /**
     * Convert all currency-containing units in measurement data to the specified currency.
     * Note: We assume that the currency unit is the numerator of a CompositeUnit. (We don't expect a unit with x/EUR for example)
     */
    public static applyCurrency(measurementData: Record<string, unknown>, currency: Unit): void {
        if (!measurementData || !currency) return;

        for (const key of Object.keys(measurementData)) {
            const value = measurementData[key];
            if (value instanceof UnitValue) {
                let newUnit: Unit = null;

                // Check if the unit is a direct currency unit
                if (value.unit.unitFamily.typeName === "Currency") {
                    newUnit = currency;
                }
                // Check if the unit is a CompositeUnit with a currency in the numerator
                else if (value.unit instanceof CompositeUnit) {
                    const compositeUnit = value.unit as CompositeUnit;
                    if (compositeUnit.numerator.unitFamily.typeName === "Currency") {
                        // Create a new CompositeUnit with the new currency as numerator
                        newUnit = new CompositeUnit(currency, compositeUnit.denominator, compositeUnit.unitType);
                    }
                }

                // Convert the value if we found a currency unit to replace
                if (newUnit && !value.unit.isLinearUnit) {
                    // Skip non-linear units to avoid conversion issues
                    continue;
                }

                if (newUnit) {
                    try {
                        measurementData[key] = new UnitValue(value.toUnit(newUnit), newUnit);
                    } catch (error) {
                        // If conversion fails, leave the original value unchanged
                        console.warn(`Failed to convert currency for ${key}:`, error);
                    }
                }
            }
        }
    }

    /**
     * Helper method to check if a value has changed (independent of unit conversion)
     */
    public static hasValueChanged(currentValue: UnitValue, originalValue: UnitValue): boolean {
        const tolerance = Math.abs(currentValue.toBaseUnit()) * 0.0001; // 0.01% tolerance relative to current value
        const currentValueBase = currentValue.toBaseUnit();
        const originalValueBase = originalValue.toBaseUnit();
        return Math.abs(currentValueBase - originalValueBase) > tolerance;
    }

    /**
     * Helper method to check if a unit has changed
     */
    public static hasUnitChanged(currentValue: UnitValue, originalValue: UnitValue): boolean {
        return currentValue.unit.unit !== originalValue.unit.unit;
    }
}
